import { secondsToMilliseconds } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { NetworkQualityStats as TwilioNetworkQualityStats } from 'twilio-video';

import { UnstableInternetToastWrapper as UnstableInternetToast } from '../components/UnstableInternetToastWrapper';
import { sendDataDogEvent } from '../datadog/events';
import { logger } from '../datadog/logger';
import { TrackingEvents, sendLoggingEvents } from '../events';
import { useAtomCallbackMemoized } from '../hooks/useAtomCallbackMemoized';
import { toast } from '../toasts';
import type { NetworkQualityChangeHandler, RemoteParticipant, RoomDelegator } from '../video-sdk';
import {
  LocalMediaStatus,
  localVideoStatusAtom,
  parseIdentity,
  participantsAtom,
  roomAtom,
  roomToDiagnosticInfo,
} from '../video-sdk';
import { TwilioRoom, getTwilioNetworkQualityStats } from '../video-sdk/twilio';
import type { ZoomNetworkQualityStatsPayload } from '../video-sdk/zoom/stats';
import { getZoomNetworkQualityStats } from '../video-sdk/zoom/stats';

const UNSTABLE_INTERNET_TOAST_KEY = 'unstable-internet-toast';

function emitNetworkQualityEvents(
  type: 'local' | 'remote',
  room: RoomDelegator,
  stats?: unknown,
  additionalCtx?: object,
) {
  if (!stats) return;

  // Each provider hands us a different stats shape; discriminate on the room
  // class (our own type, so `instanceof` is robust to esbuild renaming) and
  // flatten via the matching helper.
  const networkQualityStats =
    room instanceof TwilioRoom
      ? getTwilioNetworkQualityStats(stats as TwilioNetworkQualityStats)
      : getZoomNetworkQualityStats(stats as ZoomNetworkQualityStatsPayload);

  const ctx = {
    ...roomToDiagnosticInfo(room),
    ...additionalCtx,
    ...networkQualityStats,
  };

  sendDataDogEvent(`networkQuality.${type}`, ctx);
  logger.debug('Network quality changed', ctx);
}

const QUALITY_LEVEL_THRESHOLD = 1;
const POOR_QUALITY_DURATION_THRESHOLD = secondsToMilliseconds(15);

export function useNetworkQualityLogging({ onOpenSettings }: { onOpenSettings: () => void }) {
  const room = useAtomValue(roomAtom);
  const remoteParticipants = useAtomValue(participantsAtom);
  const getLocalVideoTrackState = useAtomCallbackMemoized((get) => get(localVideoStatusAtom), []);
  const sentNetworkQualityToastRef = useRef(false);
  const [isNetworkQualityPoor, setIsNetworkQualityPoor] = useState(true);
  const getRoom = useAtomCallbackMemoized((get) => get(roomAtom), []);

  useEffect(
    function popInternetUnstableToast() {
      if (isNetworkQualityPoor && !sentNetworkQualityToastRef.current) {
        // Trigger the toast if network quality is poor for 15 seconds and user video is on
        const timeoutId = setTimeout(() => {
          const isInRoom = getRoom();
          if (getLocalVideoTrackState() === LocalMediaStatus.READY && isInRoom) {
            sentNetworkQualityToastRef.current = true;
            sendLoggingEvents(TrackingEvents.ANY_CONNECTIVITY_ISSUE_NOTIF);
            toast.enqueue(
              (ariaProps) => (
                <UnstableInternetToast
                  {...ariaProps}
                  onOpenSettings={() => {
                    toast.close(UNSTABLE_INTERNET_TOAST_KEY);
                    onOpenSettings();
                  }}
                />
              ),
              {
                key: UNSTABLE_INTERNET_TOAST_KEY,
                timeout: Infinity,
                purpose: 'system',
              },
            );
          }
        }, POOR_QUALITY_DURATION_THRESHOLD);

        return () => {
          // If network quality improves, clear the timeout and don't trigger the toast
          clearTimeout(timeoutId);
        };
      }
    },
    [isNetworkQualityPoor, getLocalVideoTrackState, getRoom, onOpenSettings],
  );

  // Keep this subscription stable; re-subscribing resets Zoom's QoS feed.
  useEffect(
    function handleLocalNetworkQualityLevelChanges() {
      if (!room || !room.localParticipant) return;
      const localParticipant = room.localParticipant;

      const logLocalNetworkQualityStatsAndControlToast: NetworkQualityChangeHandler = (
        networkQualityLevel,
        networkQualityStats,
      ) => {
        emitNetworkQualityEvents('local', room, networkQualityStats);

        // Stop updating isNetworkQualityPoor if we aren't going to pop a toast for it
        if (sentNetworkQualityToastRef.current) {
          return;
        }

        if (networkQualityLevel > QUALITY_LEVEL_THRESHOLD) {
          setIsNetworkQualityPoor(false);
        }

        if (networkQualityLevel <= QUALITY_LEVEL_THRESHOLD) {
          setIsNetworkQualityPoor(true);
        }
      };

      const detachEventListenerCallback = localParticipant.on(
        'networkQualityLevelChanged',
        logLocalNetworkQualityStatsAndControlToast,
      );

      return function cleanup() {
        detachEventListenerCallback();
      };
    },
    [room],
  );

  useEffect(
    function handleRemoteNetworkQualityLevelChanges() {
      if (!room) return;

      const remoteParticipantNetworkQualityHandlers: Array<[RemoteParticipant, NetworkQualityChangeHandler]> = [];

      const detachListenerCallbacks = remoteParticipants.map((participant) => {
        const logRemoteNetworkQualityStats: NetworkQualityChangeHandler = (
          _networkQualityLevel,
          networkQualityStats,
        ) => {
          const { id: remoteUserId, userType: remoteUserType } = parseIdentity(participant.identity);

          emitNetworkQualityEvents('remote', room, networkQualityStats, {
            remoteParticipantSid: participant.sid,
            remoteUserId,
            remoteUserType,
          });
        };
        remoteParticipantNetworkQualityHandlers.push([participant, logRemoteNetworkQualityStats]);

        return participant.on('networkQualityLevelChanged', logRemoteNetworkQualityStats);
      });

      return function cleanup() {
        detachListenerCallbacks.forEach((detachCallback) => {
          detachCallback();
        });
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remoteParticipants, room],
  );
}
